import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Loader2, LogOut, Inbox, Download } from "lucide-react";
import { Link, useLocation } from "react-router-dom";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TYPE_LABELS = {
  service_request: "Care Request",
  caretaker_application: "Caretaker",
  nurse_application: "Nurse",
  vendor_registration: "Vendor",
  quote_request: "Quote Request",
};

const FILTERS = [
  { key: "", label: "All" },
  { key: "service_request", label: "Care Requests" },
  { key: "caretaker_application", label: "Caretakers" },
  { key: "nurse_application", label: "Nurses" },
  { key: "vendor_registration", label: "Vendors" },
  { key: "quote_request", label: "Quotes" },
];

const googleLogin = () => {
  // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
  const redirectUrl = window.location.origin + "/admin";
  window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
};

const GoogleIcon = () => (
  <svg className="h-4 w-4" viewBox="0 0 24 24">
    <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" />
    <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" />
    <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" />
    <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" />
  </svg>
);

const Admin = () => {
  const location = useLocation();
  const [token, setToken] = useState(localStorage.getItem("sharpin_admin_token") || "");
  const [googleUser, setGoogleUser] = useState(location.state?.user || null);
  const [checking, setChecking] = useState(true);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [leads, setLeads] = useState(null);
  const [filter, setFilter] = useState("");
  const [view, setView] = useState("leads");
  const [payments, setPayments] = useState(null);

  const authed = Boolean(token || (googleUser && googleUser.role === "admin"));

  useEffect(() => {
    if (token || googleUser) {
      setChecking(false);
      return;
    }
    const checkSession = async () => {
      try {
        const { data } = await axios.get(`${API}/auth/me`, { withCredentials: true });
        setGoogleUser(data);
      } catch {
        /* not signed in */
      } finally {
        setChecking(false);
      }
    };
    checkSession();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const login = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    try {
      const { data } = await axios.post(`${API}/auth/login`, { email, password });
      localStorage.setItem("sharpin_admin_token", data.access_token);
      setToken(data.access_token);
    } catch (err) {
      const detail = err.response?.data?.detail;
      setError(typeof detail === "string" ? detail : "Login failed. Check credentials.");
    } finally {
      setLoading(false);
    }
  };

  const logout = async () => {
    try {
      await axios.post(`${API}/auth/logout`, {}, { withCredentials: true });
    } catch {
      /* ignore */
    }
    localStorage.removeItem("sharpin_admin_token");
    setToken("");
    setGoogleUser(null);
    setLeads(null);
  };

  const downloadCSV = () => {
    if (!leads?.length) {
      toast.error("No leads to export.");
      return;
    }
    const keys = Array.from(new Set(leads.flatMap((l) => Object.keys(l.data))));
    const header = ["Type", "Submitted At", ...keys];
    const esc = (v) => `"${String(v ?? "").replace(/"/g, '""')}"`;
    const rows = leads.map((l) => [
      TYPE_LABELS[l.type] || l.type,
      new Date(l.created_at).toLocaleString("en-IN"),
      ...keys.map((k) => l.data[k] ?? ""),
    ]);
    const csv = [header, ...rows].map((r) => r.map(esc).join(",")).join("\n");
    const blob = new Blob(["\uFEFF" + csv], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `sharpin-leads-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
    toast.success(`${leads.length} leads exported to CSV.`);
  };

  useEffect(() => {
    if (!authed) return;
    const headers = token ? { Authorization: `Bearer ${token}` } : {};
    const fetchData = async () => {
      if (view === "leads") {
        setLeads(null);
        try {
          const { data } = await axios.get(`${API}/leads${filter ? `?type=${filter}` : ""}`, {
            headers,
            withCredentials: true,
          });
          setLeads(data);
        } catch (err) {
          if (err.response?.status === 401) logout();
          else setLeads([]);
        }
      } else {
        setPayments(null);
        try {
          const { data } = await axios.get(`${API}/payments`, { headers, withCredentials: true });
          setPayments(data);
        } catch (err) {
          if (err.response?.status === 401) logout();
          else setPayments([]);
        }
      }
    };
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, googleUser, filter, view]);

  if (checking) {
    return (
      <main className="min-h-screen bg-alabaster flex items-center justify-center" data-testid="admin-checking">
        <Loader2 className="h-8 w-8 animate-spin text-tealbrand" />
      </main>
    );
  }

  if (!authed) {
    return (
      <main className="min-h-screen bg-alabaster flex items-center justify-center px-4" data-testid="admin-login-page">
        <div className="w-full max-w-md bg-white rounded-3xl border border-slate-200 shadow-xl p-8">
          <img src="/logo.jpeg" alt="Sharpin Healthcare" className="h-16 w-16 rounded-full object-cover mx-auto mb-4" />
          <h1 className="font-display text-2xl font-bold text-navy text-center mb-1">Admin Login</h1>
          <p className="text-sm text-slate-500 text-center mb-8">Sharpin Healthcare Services — leads dashboard</p>
          {googleUser && googleUser.role !== "admin" && (
            <p className="text-sm text-red-600 text-center mb-4" data-testid="admin-not-authorized">
              {googleUser.email} is not an authorized admin account.
            </p>
          )}
          <form onSubmit={login} className="space-y-4">
            <input
              type="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="Admin email"
              data-testid="admin-email-input"
              className="w-full rounded-xl border border-slate-200 px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-tealbrand/50"
            />
            <input
              type="password"
              required
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="Password"
              data-testid="admin-password-input"
              className="w-full rounded-xl border border-slate-200 px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-tealbrand/50"
            />
            {error && <p className="text-sm text-red-600" data-testid="admin-login-error">{error}</p>}
            <button
              type="submit"
              disabled={loading}
              data-testid="admin-login-btn"
              className="w-full bg-navy text-white font-semibold py-3.5 rounded-full hover:bg-tealbrand transition-colors disabled:opacity-60 flex items-center justify-center gap-2"
            >
              {loading && <Loader2 className="h-4 w-4 animate-spin" />} Sign In
            </button>
          </form>
          <div className="flex items-center gap-3 my-5">
            <div className="flex-1 h-px bg-slate-200" />
            <span className="text-xs text-slate-400 font-semibold uppercase tracking-wider">or</span>
            <div className="flex-1 h-px bg-slate-200" />
          </div>
          <button
            onClick={googleLogin}
            data-testid="admin-google-login-btn"
            className="w-full bg-white border border-slate-200 text-navy font-semibold py-3.5 rounded-full hover:border-tealbrand hover:text-tealbrand transition-colors flex items-center justify-center gap-3"
          >
            <GoogleIcon /> Sign in with Google
          </button>
          <p className="text-[11px] text-slate-400 text-center mt-4" data-testid="admin-google-note">
            Google sign-in is restricted to authorized Sharpin email accounts.
          </p>
          <Link to="/" className="block text-center text-xs text-slate-400 mt-6 hover:text-tealbrand" data-testid="admin-back-home-link">
            Back to website
          </Link>
        </div>
      </main>
    );
  }

  return (
    <main className="min-h-screen bg-alabaster" data-testid="admin-dashboard">
      <div className="max-w-6xl mx-auto px-4 sm:px-8 py-10">
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="font-display text-2xl sm:text-3xl font-bold text-navy">Leads Dashboard</h1>
            <p className="text-sm text-slate-500 mt-1" data-testid="admin-signed-in-as">
              All form submissions from the website{googleUser?.email ? ` · signed in as ${googleUser.email}` : ""}
            </p>
          </div>
          <div className="flex items-center gap-3">
            {view === "leads" && (
              <button
                onClick={downloadCSV}
                data-testid="admin-download-csv-btn"
                className="inline-flex items-center gap-2 bg-white border border-slate-200 text-navy text-sm font-semibold px-5 py-2.5 rounded-full hover:border-tealbrand hover:text-tealbrand transition-colors"
              >
                <Download className="h-4 w-4" /> Download CSV
              </button>
            )}
            <Link to="/" className="text-sm font-semibold text-slate-500 hover:text-tealbrand" data-testid="admin-home-link">
              Website
            </Link>
            <button
              onClick={logout}
              data-testid="admin-logout-btn"
              className="inline-flex items-center gap-2 bg-navy text-white text-sm font-semibold px-5 py-2.5 rounded-full hover:bg-red-600 transition-colors"
            >
              <LogOut className="h-4 w-4" /> Logout
            </button>
          </div>
        </div>

        <div className="flex gap-2 mb-6" data-testid="admin-view-toggle">
          <button
            onClick={() => setView("leads")}
            data-testid="admin-view-leads"
            className={`text-sm font-semibold px-5 py-2 rounded-full transition-colors ${view === "leads" ? "bg-tealbrand text-white" : "bg-white border border-slate-200 text-slate-600 hover:border-tealbrand"}`}
          >
            Leads
          </button>
          <button
            onClick={() => setView("payments")}
            data-testid="admin-view-payments"
            className={`text-sm font-semibold px-5 py-2 rounded-full transition-colors ${view === "payments" ? "bg-tealbrand text-white" : "bg-white border border-slate-200 text-slate-600 hover:border-tealbrand"}`}
          >
            Payments
          </button>
        </div>

        {view === "leads" && (
        <div className="flex flex-wrap gap-2 mb-8" data-testid="admin-filters">
          {FILTERS.map((f) => (
            <button
              key={f.key}
              onClick={() => setFilter(f.key)}
              data-testid={`admin-filter-${f.label.toLowerCase().replace(/[^a-z]+/g, "-")}`}
              className={`text-sm font-semibold px-4 py-2 rounded-full transition-colors ${
                filter === f.key ? "bg-navy text-white" : "bg-white border border-slate-200 text-slate-600 hover:border-tealbrand"
              }`}
            >
              {f.label}
            </button>
          ))}
        </div>
        )}

        {view === "leads" && (leads === null ? (
          <div className="flex justify-center py-20" data-testid="admin-loading">
            <Loader2 className="h-8 w-8 animate-spin text-tealbrand" />
          </div>
        ) : leads.length === 0 ? (
          <div className="text-center py-20 text-slate-400" data-testid="admin-empty">
            <Inbox className="h-10 w-10 mx-auto mb-3" />
            No submissions yet.
          </div>
        ) : (
          <div className="space-y-4" data-testid="admin-leads-list">
            {leads.map((lead) => (
              <div key={lead.id} className="bg-white rounded-2xl border border-slate-200 p-6" data-testid={`admin-lead-${lead.id}`}>
                <div className="flex flex-wrap items-center justify-between gap-2 mb-4">
                  <span className="text-xs font-bold uppercase tracking-[0.15em] bg-tealbrand-light text-tealbrand-dark px-3 py-1.5 rounded-full">
                    {TYPE_LABELS[lead.type] || lead.type}
                  </span>
                  <span className="text-xs text-slate-400">
                    {new Date(lead.created_at).toLocaleString("en-IN")}
                  </span>
                </div>
                <dl className="grid sm:grid-cols-2 lg:grid-cols-3 gap-x-8 gap-y-3">
                  {Object.entries(lead.data).map(([k, v]) => (
                    <div key={k}>
                      <dt className="text-[11px] uppercase tracking-wider font-bold text-slate-400">{k}</dt>
                      <dd className="text-sm text-navy font-medium break-words">{v || "—"}</dd>
                    </div>
                  ))}
                </dl>
              </div>
            ))}
          </div>
        ))}

        {view === "payments" && (
          payments === null ? (
            <div className="flex justify-center py-20" data-testid="admin-payments-loading">
              <Loader2 className="h-8 w-8 animate-spin text-tealbrand" />
            </div>
          ) : payments.length === 0 ? (
            <div className="text-center py-20 text-slate-400" data-testid="admin-payments-empty">
              <Inbox className="h-10 w-10 mx-auto mb-3" />
              No payments yet.
            </div>
          ) : (
            <div className="space-y-4" data-testid="admin-payments-list">
              {payments.map((p) => (
                <div key={p.order_id} className="bg-white rounded-2xl border border-slate-200 p-6" data-testid={`admin-payment-${p.order_id}`}>
                  <div className="flex flex-wrap items-center justify-between gap-2 mb-3">
                    <span className={`text-xs font-bold uppercase tracking-[0.15em] px-3 py-1.5 rounded-full ${p.status === "paid" ? "bg-tealbrand-light text-tealbrand-dark" : "bg-slate-100 text-slate-500"}`}>
                      {p.status}
                    </span>
                    <span className="text-xs text-slate-400">{new Date(p.created_at).toLocaleString("en-IN")}</span>
                  </div>
                  <div className="flex flex-wrap items-baseline gap-x-8 gap-y-2">
                    <span className="font-display text-xl font-bold text-navy">₹{(p.amount / 100).toLocaleString("en-IN")}</span>
                    {p.name && <span className="text-sm text-navy font-medium">{p.name}</span>}
                    {p.phone && <span className="text-sm text-slate-600">{p.phone}</span>}
                    {p.terms_accepted && <span className="text-xs font-semibold text-tealbrand-dark">Terms accepted ✓</span>}
                    <span className="text-xs text-slate-400">Order: {p.order_id}</span>
                    {p.payment_id && <span className="text-xs text-slate-400">Payment: {p.payment_id}</span>}
                  </div>
                </div>
              ))}
            </div>
          )
        )}
      </div>
    </main>
  );
};

export default Admin;
